import imageCompression from 'browser-image-compression';
import { UploadFile } from 'components/applets/app-upload';

export interface ImageCompressionOptions {
  maxSizeMB?: number; // e.g., 0.3 for 300 KB
  maxWidthOrHeight?: number; // e.g., 1920
  useWebWorker?: boolean;
  fileType?: string; // e.g., 'image/jpeg'
  initialQuality?: number; // 0-1
}

export interface ImageMetadata {
  width?: number;
  height?: number;
  size?: number; // in bytes
  originalSize?: number; // in bytes
}

const MAX_IMAGE_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_COMPRESSED_SIZE = 300 * 1024; // 300 KB
const MAX_RESOLUTION = 1920; // 1920x1080 max
const WARNING_SIZE_THRESHOLD = 10 * 1024 * 1024; // 10 MB - show warning

/**
 * Determines image compression quality based on file size
 * @param fileSizeMB File size in MB
 * @returns Compression quality (0-1) according to size range
 */
export const getImageQualityBySize = (fileSizeMB: number): number => {
  if (fileSizeMB <= 2) {
    // 0-2 MB: quality 92-95% (avg 0.935)
    return 0.935;
  } else if (fileSizeMB <= 10) {
    // 2-10 MB: quality 85-92% (avg 0.885)
    return 0.885;
  } else if (fileSizeMB <= 25) {
    // 10-25 MB: quality 75-85% (avg 0.80)
    return 0.80;
  } else if (fileSizeMB <= 50) {
    // 25-50 MB: quality 65-75% (avg 0.70)
    return 0.70;
  } else {
    // 50-100 MB: quality 55-65% (avg 0.60)
    return 0.60;
  }
};

/**
 * Checks if a file is an image
 */
export const isImageFile = (file: UploadFile): boolean => {
  const mimeType = file.mime || file.type || '';
  return mimeType.includes('image');
};

/**
 * Gets basic file information (size and dimensions)
 */
export const getImageFileInfo = async (
  file: UploadFile,
): Promise<ImageMetadata | null> => {
  try {
    let blob: Blob;
    
    // If we have the original File object, use it directly (more reliable and efficient)
    if (file.originalFile) {
      blob = file.originalFile;
    } else {
      // Otherwise, try to fetch from URI
      const response = await fetch(file.uri);
      blob = await response.blob();
    }

    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          size: blob.size,
          originalSize: blob.size,
        });
        URL.revokeObjectURL(url);
      };

      img.onerror = () => {
        // If we can't get dimensions, at least return the size
        resolve({
          size: blob.size,
          originalSize: blob.size,
        });
        URL.revokeObjectURL(url);
      };

      img.src = url;
    });
  } catch (error) {
    console.error('Error getting image info:', error);
    return null;
  }
};

/**
 * Determines if an image needs compression based on its size or resolution
 */
export const needsCompression = async (file: UploadFile): Promise<boolean> => {
  // If it's not an image, it doesn't need compression
  if (!isImageFile(file)) {
    return false;
  }

  // Get file information
  const fileInfo = await getImageFileInfo(file);
  if (!fileInfo) {
    // If we can't get info, assume it needs compression for safety
    return true;
  }

  // Check maximum size
  if (fileInfo.size && fileInfo.size > MAX_COMPRESSED_SIZE) {
    return true;
  }

  // Check maximum resolution
  if (
    fileInfo.width &&
    fileInfo.height &&
    (fileInfo.width > MAX_RESOLUTION || fileInfo.height > MAX_RESOLUTION)
  ) {
    return true;
  }

  return false;
};

/**
 * Determines if an image is very large and needs a warning
 */
export const needsWarning = async (
  file: UploadFile,
): Promise<{ needsWarning: boolean; message?: string }> => {
  if (!isImageFile(file)) {
    return { needsWarning: false };
  }

  const fileInfo = await getImageFileInfo(file);
  if (!fileInfo || !fileInfo.size) {
    return { needsWarning: false };
  }

  if (fileInfo.size > WARNING_SIZE_THRESHOLD) {
    return {
      needsWarning: true,
      message: 'This is a large file, it might take longer to upload.',
    };
  }

  return { needsWarning: false };
};

/**
 * Validates if an image can be uploaded
 * Returns an error if the image is too large even for compression
 */
export const validateImageForUpload = async (
  file: UploadFile,
): Promise<{ valid: boolean; error?: string }> => {
  if (!isImageFile(file)) {
    return { valid: true };
  }

  const fileInfo = await getImageFileInfo(file);
  if (!fileInfo) {
    // If we can't get the file size (can happen with very large files),
    // allow it to continue. Size will be validated when the blob is obtained for upload.
    console.warn('[VALIDATE] Could not get image size, will validate during upload');
    return { valid: true };
  }

  // If the file is larger than 100 MB, reject it
  if (fileInfo.size && fileInfo.size > MAX_IMAGE_SIZE) {
    return {
      valid: false,
      error: 'Your file exceeds the size limit (100 mb for images, 10,000 mb for video). Please try again',
    };
  }

  return { valid: true };
};

/**
 * Compresses an image automatically
 * Resizes if necessary and compresses to maximum 300 KB
 */
export const compressImage = async (
  file: UploadFile,
  options: Partial<ImageCompressionOptions> = {},
  onProgress?: (progress: number) => void,
): Promise<UploadFile> => {
  // Validate the file
  const validation = await validateImageForUpload(file);
  if (!validation.valid) {
    throw new Error(validation.error || 'The image is not valid for upload');
  }

  // Get image information
  const fileInfo = await getImageFileInfo(file);
  if (!fileInfo) {
    throw new Error('Could not get image information');
  }

  // Determine if compression or resizing is needed
  const needsComp = await needsCompression(file);
  if (!needsComp) {
    // Doesn't need compression, return original file
    if (onProgress) {
      onProgress(100);
    }
    return file;
  }

  // Get the file as Blob
  const response = await fetch(file.uri);
  const blob = await response.blob();
  const originalFile = new File([blob], file.name || 'image.jpg', {
    type: file.mime || file.type || 'image/jpeg',
  });

  // Calculate adaptive quality based on file size
  const fileSizeMB = fileInfo.size ? fileInfo.size / (1024 * 1024) : 0;
  const adaptiveQuality = getImageQualityBySize(fileSizeMB);

  // Configure compression options
  const compressionOptions: ImageCompressionOptions = {
    maxSizeMB: MAX_COMPRESSED_SIZE / (1024 * 1024), // 300 KB in MB
    maxWidthOrHeight: MAX_RESOLUTION, // 1920x1080 max
    useWebWorker: true,
    fileType: file.mime || file.type || 'image/jpeg',
    initialQuality: adaptiveQuality, // Adaptive quality based on size
    ...options,
  };


  try {
    const compressedFile = await imageCompression(originalFile, compressionOptions);

    // Create compressed file URL
    const compressedUrl = URL.createObjectURL(compressedFile);

    if (onProgress) {
      onProgress(100);
    }

    // Return the compressed file
    return {
      ...file,
      uri: compressedUrl,
      mime: compressedFile.type,
      type: compressedFile.type,
    };
  } catch (error) {
    console.error('[IMAGE_COMPRESS] Error compressing image:', {
      error,
      fileName: file.name,
      fileSizeMB: fileInfo.size ? (fileInfo.size / (1024 * 1024)).toFixed(2) : 'N/A',
    });
    // If compression fails, return the original file
    if (onProgress) {
      onProgress(100);
    }
    return file;
  }
};